import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDashboardStats } from '../features/customers/customersAPI';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

export default function Dashboard() {
  const { isAuthenticated, user } = useAuth();
  const dispatch = useDispatch();
  const { customers, total } = useSelector((state) => state.customers);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const data = await fetchDashboardStats();
      setStats(data.stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadStats();
    }
  }, [dispatch, isAuthenticated, loadStats]);

  const primaryColor = '#05aa4be6';
  const darkBlue = '#000036';
  const COLORS = ['#05aa4be6', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#fd7e14'];

  return (
    <div className="container-fluid">
      {isAuthenticated ? (
        <>
          {/* Welcome Header */}
          <div className="card border-0 shadow-sm mb-4" style={{
            backgroundColor: darkBlue,
            color: 'white',
            borderRadius: '0.5rem'
          }}>
            <div className="card-body p-4">
              <h2 className="mb-2 d-flex align-items-center gap-2">
                <svg width="28" height="28" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                </svg>
                Welcome back, {user?.name || user?.email?.split('@')[0]}!
              </h2>
              <p className="mb-0" style={{ opacity: 0.8 }}>Here's what's happening with your system today</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100" style={{
                backgroundColor: primaryColor,
                borderRadius: '0.5rem'
              }}>
                <div className="card-body text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase mb-2" style={{ opacity: 0.8 }}>Total Customers</h6>
                      <h2 className="mb-0 fw-bold">{stats?.totalCustomers || 0}</h2>
                    </div>
                    <div style={{ opacity: 0.3 }}>
                      <svg width="60" height="60" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100" style={{
                backgroundColor: '#28a745',
                borderRadius: '0.5rem'
              }}>
                <div className="card-body text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase mb-2" style={{ opacity: 0.8 }}>Active Customers</h6>
                      <h2 className="mb-0 fw-bold">
                        {stats?.byStatus?.find(s => s._id === 'active')?.count || 0}
                      </h2>
                    </div>
                    <div style={{ opacity: 0.3 }}>
                      <svg width="60" height="60" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100" style={{
                backgroundColor: '#dc3545',
                borderRadius: '0.5rem'
              }}>
                <div className="card-body text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-uppercase mb-2" style={{ opacity: 0.8 }}>Inactive Customers</h6>
                      <h2 className="mb-0 fw-bold">
                        {stats?.byStatus?.find(s => s._id === 'inactive')?.count || 0}
                      </h2>
                    </div>
                    <div style={{ opacity: 0.3 }}>
                      <svg width="60" height="60" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Migration Categories Section */}
          {stats && !loadingStats && stats.byCategory && stats.byCategory.length > 0 && (
            <div className="row g-4 mb-4">
              {stats.byCategory.map((category, index) => (
                <div key={category._id} className="col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm h-100" style={{
                    borderRadius: '0.5rem',
                    borderLeft: `4px solid ${COLORS[index % COLORS.length]}`
                  }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h6 className="text-uppercase mb-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                            {category._id}
                          </h6>
                          <h3 className="mb-0 fw-bold" style={{ color: COLORS[index % COLORS.length] }}>
                            {category.count}
                          </h3>
                        </div>
                        <div style={{ opacity: 0.2 }}>
                          <svg width="50" height="50" viewBox="0 0 16 16" fill={COLORS[index % COLORS.length]}>
                            <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="d-flex gap-3">
                        <div>
                          <small className="text-muted d-block">Active</small>
                          <strong className="text-success">{category.active}</strong>
                        </div>
                        <div>
                          <small className="text-muted d-block">Inactive</small>
                          <strong className="text-danger">{category.inactive}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dashboard Statistics Charts */}
          {stats && !loadingStats && (
            <>
              {/* Database Clusters and Instances - Side by Side */}
              <div className="row g-4 mb-4">
                <div className="col-lg-6">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '0.5rem' }}>
                    <div className="card-body">
                      <h5 className="card-title mb-4" style={{ color: primaryColor }}>
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M8 1c-1.573 0-3.022.289-4.096.777C2.875 2.245 2 2.993 2 4s.875 1.755 1.904 2.223C4.978 6.711 6.427 7 8 7s3.022-.289 4.096-.777C13.125 5.755 14 5.007 14 4s-.875-1.755-1.904-2.223C11.022 1.289 9.573 1 8 1z"/>
                          <path d="M2 7v-.839c.457.432 1.004.751 1.49.972C4.722 7.693 6.318 8 8 8s3.278-.307 4.51-.867c.486-.22 1.033-.54 1.49-.972V7c0 .424-.155.802-.411 1.133a4.51 4.51 0 0 0-1.036 1.272c-.585.945-1.163 2.054-1.163 2.595v1.5c0 .546-.452.988-1 .988h-2.5c-.548 0-1-.442-1-.988V12c0-.541-.578-1.65-1.163-2.595a4.51 4.51 0 0 0-1.036-1.272A1.796 1.796 0 0 1 2 7z"/>
                        </svg>
                        By Database Cluster
                      </h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={stats.byCluster.map(item => ({
                          name: item._id || 'Unknown',
                          active: item.active,
                          inactive: item.inactive
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="active" fill="#28a745" name="Active" />
                          <Bar dataKey="inactive" fill="#dc3545" name="Inactive" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '0.5rem' }}>
                    <div className="card-body">
                      <h5 className="card-title mb-4" style={{ color: primaryColor }}>
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                          <path d="M2.165 15.803.02-.004C1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a10.97 10.97 0 0 0 .398-2z"/>
                        </svg>
                        By Instance Name
                      </h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={stats.byInstance.map(item => ({
                          name: item._id || 'Unknown',
                          active: item.active,
                          inactive: item.inactive
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="active" fill="#05aa4be6" name="Active" />
                          <Bar dataKey="inactive" fill="#ffc107" name="Inactive" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Migration Categories Chart */}
              {stats.byCategory && stats.byCategory.length > 0 && (
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '0.5rem' }}>
                    <div className="card-body">
                      <h5 className="card-title mb-4" style={{ color: primaryColor }}>
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z"/>
                        </svg>
                        Customers by Migration Category
                      </h5>
                      <div className="row">
                        <div className="col-lg-6">
                          <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                              <Pie
                                data={stats.byCategory.map(item => ({
                                  name: item._id || 'Unknown',
                                  value: item.count
                                }))}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {stats.byCategory.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="col-lg-6">
                          <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={stats.byCategory.map(item => ({
                              name: item._id || 'Unknown',
                              active: item.active,
                              inactive: item.inactive,
                              total: item.count
                            }))}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="active" stackId="a" fill="#28a745" name="Active" />
                              <Bar dataKey="inactive" stackId="a" fill="#dc3545" name="Inactive" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
              )}

            </>
          )}

          {/* Info Section */}
          <div className="row g-4">
            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '0.5rem' }}>
                <div className="card-body">
                  <h5 className="card-title d-flex align-items-center gap-2" style={{ color: primaryColor }}>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                    </svg>
                    Quick Tips
                  </h5>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2 d-flex align-items-start gap-2">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ marginTop: '2px' }}>
                        <path fillRule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                        <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5z"/>
                      </svg>
                      Use the search feature to quickly find customers
                    </li>
                    <li className="mb-2 d-flex align-items-start gap-2">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ marginTop: '2px' }}>
                        <path fillRule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                        <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5z"/>
                      </svg>
                      Filter customers by status for better organization
                    </li>
                    <li className="mb-2 d-flex align-items-start gap-2">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ marginTop: '2px' }}>
                        <path fillRule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                        <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5z"/>
                      </svg>
                      Click on any customer to view detailed information
                    </li>
                    <li className="mb-0 d-flex align-items-start gap-2">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ marginTop: '2px' }}>
                        <path fillRule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                        <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5z"/>
                      </svg>
                      Changes are saved automatically
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '0.5rem' }}>
                <div className="card-body">
                  <h5 className="card-title d-flex align-items-center gap-2" style={{ color: primaryColor }}>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                    </svg>
                    Your Account
                  </h5>
                  <div className="mb-2">
                    <strong>Email:</strong> {user?.email}
                  </div>
                  <div className="mb-2">
                    <strong>Role:</strong>{' '}
                    <span className="badge text-capitalize text-white" style={{
                      backgroundColor: primaryColor,
                      borderRadius: '0.5rem',
                      padding: '0.35rem 0.75rem'
                    }}>
                      {user?.role}
                    </span>
                  </div>
                  <div className="mb-2">
                    <strong>Status:</strong>{' '}
                    <span className="badge text-white d-inline-flex align-items-center gap-1" style={{
                      backgroundColor: '#28a745',
                      borderRadius: '0.5rem',
                      padding: '0.35rem 0.75rem'
                    }}>
                      Active
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card border-0 shadow-lg" style={{
              backgroundColor: darkBlue,
              color: 'white',
              borderRadius: '0.5rem'
            }}>
              <div className="card-body text-center p-5">
                <div className="mb-3">
                  <svg width="80" height="80" viewBox="0 0 16 16" fill="currentColor" style={{ opacity: 0.9 }}>
                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                  </svg>
                </div>
                <h2 className="mb-3">Welcome to Arborgold Atlas</h2>
                <p className="mb-4" style={{ opacity: 0.8 }}>Please login to access your dashboard and manage customers</p>
                <Link to="/login" className="btn btn-lg px-5 text-white d-inline-flex align-items-center gap-2" style={{
                  backgroundColor: primaryColor,
                  border: 'none',
                  borderRadius: '0.5rem'
                }}>
                  Login Now
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
